import { memo, useCallback, useState, useRef } from 'react';
import { JackpotLabelProps } from './JackpotLabel.types';
import { Box, CountUp } from '@bonanza-social/moonlight';
import { formatNumber } from '@bonanza-social/toolkit/utils';
import { useSelector } from '@bonanza-social/toolkit/state';
import { selectJackpot } from '../../../../state/jackpots/jackpots.slice';
import { shouldShowDecimals } from './JackpotLabel.utils';
import { JackpotTextVariant } from './JackpotLabel.constants';
import { useFocusEffect } from '@bonanza-social/navigation';

function JackpotLabel({ gameId, imageType }: JackpotLabelProps) {
  const jackpot = useSelector(selectJackpot(gameId));
  const showDecimals = shouldShowDecimals(jackpot, imageType);
  const [isVisible, setIsVisible] = useState(false);
  const startAmount: number = showDecimals
    ? Math.min(jackpot?.startAmount, jackpot?.endAmount)
    : Math.min(
        Math.floor(jackpot?.startAmount),
        Math.floor(jackpot?.endAmount),
      );

  const lastValue = useRef<number>(startAmount);

  const formatValue = useCallback(
    (amount: number) =>
      formatNumber(amount, {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: showDecimals ? undefined : 0,
      }),
    [showDecimals],
  );

  const handleValueChange = useCallback((value: number) => {
    lastValue.current = value;
  }, []);

  useFocusEffect(
    useCallback(() => {
      setIsVisible(true);
      return () => {
        setIsVisible(false);
      };
    }, []),
  );

  if (!jackpot || !isVisible) {
    return null;
  }

  return (
    <Box
      position="absolute"
      left="space-0"
      right="space-0"
      bottom="space-0"
      justifyContent="center"
      backgroundColor="backdropSecondary"
      borderBottomLeftRadius="radius-400"
      borderBottomRightRadius="radius-400"
      paddingVertical="space-100"
      renderToHardwareTextureAndroid
    >
      <CountUp
        variant={JackpotTextVariant[imageType]}
        color="level_2"
        paddingHorizontal="space-200"
        alignSelf={imageType === 'square' ? 'flex-start' : 'center'}
        fontVariant={['tabular-nums']}
        formatValue={formatValue}
        start={Math.max(lastValue.current, startAmount)}
        end={
          showDecimals
            ? Math.max(
                jackpot.startAmount + jackpot.stepPerSecond,
                jackpot.endAmount,
              )
            : Math.max(
                Math.floor(jackpot.startAmount + jackpot.stepPerSecond),
                Math.floor(jackpot.endAmount),
              )
        }
        increment={jackpot.stepPerSecond}
        useVisualIncrement={true}
        onValueChange={handleValueChange}
      />
    </Box>
  );
}

export default memo(JackpotLabel);
